<template>
  <div class="content terms-page">
    <div class="terms-shell">
      <div class="terms-header">
        <h1 class="page-title">버전 이력</h1>
        <p class="page-sub" v-if="document">
          {{ document.locale }} · {{ document.type }} · 현재
          <strong class="v-main">{{ document.currentVersionLabel || "v" + document.currentVersionNo }}</strong>
          <span v-if="document.currentVersionLabel" class="v-sub">v{{ document.currentVersionNo }}</span>
        </p>
      </div>

      <div class="toolbar">
        <div class="toolbar-l">
          <button class="btn" @click="goList">목록</button>
          <button class="btn" @click="goEdit">편집</button>
        </div>
      </div>

      <el-table class="terms-table versions-table" :data="versions" v-loading="isLoading">
        <el-table-column label="버전" width="110" class-name="col-version">
          <template slot-scope="props">
            <span class="v-cell">
              <strong class="v-main">{{ props.row.versionLabel || "v" + props.row.versionNo }}</strong>
              <span v-if="props.row.versionLabel" class="v-sub">v{{ props.row.versionNo }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100" class-name="col-status">
          <template slot-scope="props">
            <span :class="['status', statusClass(props.row.status)]">{{ statusLabel[props.row.status] || props.row.status }}</span>
          </template>
        </el-table-column>
        <el-table-column label="발행일" width="170" class-name="col-datetime">
          <template slot-scope="props">
            <span class="muted">{{ formatTime(props.row.publishedAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="publishedBy" label="발행자" width="130">
          <template slot-scope="props">
            <span class="muted">{{ props.row.publishedBy || "—" }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="note" label="메모" min-width="200" show-overflow-tooltip />
        <el-table-column label="작업" width="140" align="right" class-name="actions-col col-actions">
          <template slot-scope="props">
            <button class="btn-text btn-sm" @click="openBodyDialog(props.row)">본문</button>
            <button v-if="canRollback(props.row)" class="btn-text btn-sm danger" @click="openRollbackDialog(props.row)">롤백</button>
            <span v-else-if="props.row.status === 'FAILED'" class="muted failed-tag" title="발행 실패 — 롤백 불가">실패</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="row mt-3" v-if="totalPages > 1">
        <base-pagination class="pagination-no-border" v-model="currentPage" :pageCount="totalPages" />
      </div>
    </div>

    <el-dialog :visible.sync="bodyVisible" width="80%" title="버전 본문" :append-to-body="true" custom-class="terms-dialog">
      <div v-if="bodyLoading" class="dialog-progress"><i class="el-icon-loading"></i> 로딩 중...</div>
      <iframe v-else class="version-body-frame" :srcdoc="bodySrcDoc" sandbox="allow-same-origin" referrerpolicy="no-referrer"></iframe>
      <span slot="footer">
        <button class="btn" @click="bodyVisible = false">닫기</button>
      </span>
    </el-dialog>

    <el-dialog :visible.sync="rollbackVisible" width="480px" title="버전 롤백" :append-to-body="true" custom-class="terms-dialog">
      <p class="muted dialog-paragraph">
        선택한 버전(<b v-if="rollbackTarget"
          >v{{ rollbackTarget.versionNo }}<span v-if="rollbackTarget.versionLabel"> ({{ rollbackTarget.versionLabel }})</span></b
        >)으로 롤백합니다. latest URL이 즉시 갱신되며 롤백도 새 버전으로 기록됩니다.
      </p>
      <p class="dialog-transition" v-if="document">{{ rollbackTransitionLabel }}</p>
      <el-form label-position="top">
        <el-form-item label="버전 라벨 (선택, 비우면 자동 minor++)">
          <el-input v-model="rollbackVersionLabel" :placeholder="rollbackLabelPlaceholder" />
          <div class="dialog-hint">{{ rollbackLabelHint }}</div>
        </el-form-item>
        <el-form-item label="롤백 사유 (필수)" required>
          <el-input type="textarea" v-model="rollbackNote" :rows="3" placeholder="롤백 사유를 입력해주세요" />
        </el-form-item>
      </el-form>
      <div v-if="isRollingBack" class="dialog-progress">
        <i class="el-icon-loading"></i>
        롤백 진행 중...
      </div>
      <span slot="footer">
        <button class="btn" :disabled="isRollingBack" @click="rollbackVisible = false">취소</button>
        <button class="btn btn-primary danger-primary" :disabled="isRollingBack" @click="onRollbackConfirm">롤백 실행</button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import { Table, TableColumn, Dialog, Form, FormItem, Input } from "element-ui";
import { BasePagination } from "@/components/index";
import { formatDateTime } from "@/util/DateUtils";
import { showAPIErrorToast } from "@/util/ErrorUtils";

export default {
  components: {
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Input.name]: Input,
  },
  async created() {
    this.termsId = this.$route.params.id;
    await Promise.all([this.fetchDocument(), this.fetchVersions()]);
  },
  data() {
    return {
      termsId: null,
      document: null,
      versions: [],
      currentPage: 1,
      totalPages: 1,
      isLoading: false,
      bodyVisible: false,
      bodyLoading: false,
      bodySrcDoc: "",
      rollbackVisible: false,
      rollbackTarget: null,
      rollbackNote: "",
      rollbackVersionLabel: "",
      isRollingBack: false,
      statusLabel: {
        NEVER_PUBLISHED: "미발행",
        PUBLISHED: "발행됨",
        PUBLISHING: "발행 중",
        FAILED: "실패",
      },
    };
  },
  watch: {
    currentPage() {
      this.fetchVersions();
    },
  },
  computed: {
    // H2 — show "현재: 1.7 → 다음: 1.8 (자동)" or "직접 입력 필요" inside the
    // rollback dialog so the operator knows what label will be assigned.
    rollbackTransitionLabel() {
      if (!this.document) return "";
      const cur = this.document.currentVersionLabel;
      if (!cur) return "신규 v1 (자동)";
      const next = this.predictNextLabel(cur);
      if (next) return `현재 v${cur} → 다음 v${next} (자동)`;
      return `현재 v${cur} → 직접 입력 필요`;
    },
    rollbackLabelPlaceholder() {
      if (!this.document) return "예: 1.0";
      const lastLabel = this.document.currentVersionLabel;
      if (!lastLabel) return "예: 1.0";
      const next = this.predictNextLabel(lastLabel);
      if (next) return `예: ${next}`;
      return "예: 1.0 (이전 라벨이 비semver라 직접 입력 필요)";
    },
    rollbackLabelHint() {
      const lastLabel = this.document && this.document.currentVersionLabel;
      if (!lastLabel) {
        return "이 문서에 라벨이 없으니 직접 입력하거나 비워두세요.";
      }
      const next = this.predictNextLabel(lastLabel);
      if (next) {
        return `이전 라벨이 ${lastLabel} (semver) → 비우면 자동으로 ${next}`;
      }
      return `이전 라벨이 ${lastLabel} (비semver) → 자동 증가 불가, 라벨 직접 입력 필요`;
    },
  },
  methods: {
    formatTime(value) {
      if (!value) return "—";
      return formatDateTime(value);
    },
    predictNextLabel(label) {
      // Mirror of TermsForm.vue helper — semver (X.Y or X.Y.Z) → minor++ preview.
      const m = /^(\d+)\.(\d+)(\.(\d+))?$/.exec(label || "");
      if (!m) return null;
      const major = m[1];
      const minor = parseInt(m[2], 10) + 1;
      return `${major}.${minor}`;
    },
    statusClass(status) {
      switch (status) {
        case "PUBLISHED":
          return "published";
        case "NEVER_PUBLISHED":
          return "never-published";
        case "PUBLISHING":
          return "publishing";
        case "FAILED":
          return "failed";
        default:
          return "never-published";
      }
    },
    canRollback(row) {
      if (!row || row.status !== "PUBLISHED") return false;
      if (this.document && this.document.currentVersionNo === row.versionNo) return false;
      return true;
    },
    async fetchDocument() {
      try {
        const res = await this.$axios.get(`/terms/${this.termsId}`);
        this.document = res.data.data || res.data;
      } catch (err) {
        showAPIErrorToast(err);
      }
    },
    async fetchVersions() {
      this.isLoading = true;
      try {
        // H3 — server reads `page` (TermsController parses req.query.page);
        // sending `currentPage` silently fell back to page=1 on every paged
        // navigation, breaking pagination above page 1.
        const params = { page: this.currentPage };
        const res = await this.$axios.get(`/terms/${this.termsId}/versions`, { params });
        const payload = res.data.data || res.data;
        if (Array.isArray(payload)) {
          this.versions = payload;
          this.totalPages = 1;
        } else {
          this.versions = payload.data || payload.versions || payload.items || [];
          // LOW — `payload.page` is the *current* page (1, 2, ...), not the
          // total. Reading it as totalPages collapsed pagination to 1 on page
          // 1 and to 2 on page 2, etc. Prefer explicit `totalPages`; fall
          // back to `Math.ceil(total / limit)` if the server only returns
          // total + page size; otherwise default to 1.
          if (payload.totalPages != null) {
            this.totalPages = Number(payload.totalPages) || 1;
          } else if (payload.total != null) {
            const limit = Number(payload.limit || payload.itemsPerPage || payload.pageSize || 20);
            const total = Number(payload.total) || 0;
            this.totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
          } else {
            this.totalPages = 1;
          }
        }
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isLoading = false;
      }
    },
    async openBodyDialog(row) {
      this.bodyVisible = true;
      this.bodyLoading = true;
      this.bodySrcDoc = "";
      try {
        const res = await this.$axios.get(`/terms/${this.termsId}/preview`, {
          params: { source: "snapshot", versionNo: row.versionNo },
          responseType: "text",
          transformResponse: [(d) => d],
        });
        const html = typeof res.data === "string" ? res.data : "";
        this.bodySrcDoc = html;
      } catch (err) {
        // 서버가 source=version을 지원하지 않을 수 있음 — 안내 메시지로 대체
        const fallback = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>v${
          row.versionNo
        }</title></head><body style="font-family:sans-serif;padding:16px;"><p>본문 미리보기를 불러올 수 없습니다.</p><p>S3 스냅샷 키: <code>${
          row.s3SnapshotKey || "-"
        }</code></p><p>bodyHash: <code>${row.bodyHash || "-"}</code></p></body></html>`;
        this.bodySrcDoc = fallback;
        showAPIErrorToast(err);
      } finally {
        this.bodyLoading = false;
      }
    },
    openRollbackDialog(row) {
      this.rollbackTarget = row;
      this.rollbackNote = "";
      this.rollbackVersionLabel = "";
      this.rollbackVisible = true;
    },
    async onRollbackConfirm() {
      if (!this.rollbackTarget) return;
      if (!this.rollbackNote || !this.rollbackNote.trim()) {
        window.Toast.error("롤백 사유를 입력해주세요");
        return;
      }
      this.isRollingBack = true;
      try {
        const url = `/terms/${this.termsId}/versions/${this.rollbackTarget.versionNo}/rollback`;
        const body = { note: this.rollbackNote };
        if (this.rollbackVersionLabel && this.rollbackVersionLabel.trim()) {
          body.versionLabel = this.rollbackVersionLabel.trim();
        }
        await this.$axios.post(url, body);
        window.Toast.success("롤백되었습니다");
        this.rollbackVisible = false;
        this.rollbackTarget = null;
        await Promise.all([this.fetchDocument(), this.fetchVersions()]);
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isRollingBack = false;
      }
    },
    goList() {
      this.$router.push("/terms/list");
    },
    goEdit() {
      this.$router.push(`/terms/${this.termsId}/edit`);
    },
  },
};
</script>
<style scoped>
/* .terms-page wrapper limits readability tweaks (font-size + line-height) to Terms pages.
   Fluid scaling via clamp(): smooth growth from laptop (~1280px) to large monitor (1920px+). */
.terms-page {
  font-size: clamp(14px, 1vw, 19px);
  line-height: 1.6;
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.terms-shell {
  /* RETRY 19 — display:block + clamped padding-bottom mirrors Terms.vue.
     Prevents flex parent from stretching el-table into an empty area
     below the last row. */
  display: block;
  padding: 24px clamp(16px, 2.5vw, 64px);
  padding-bottom: clamp(16px, 1.5vw, 32px);
  max-width: clamp(960px, 95vw, 2400px);
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Apple SD Gothic Neo", sans-serif;
  color: var(--text-primary);
}
.terms-header {
  margin-bottom: 16px;
}
/* !important needed to override element-ui white-dashboard .content layer
   which forces a lighter text color on body content. */
.terms-page .page-title {
  font-size: clamp(20px, 1.8vw, 32px);
  font-weight: 700;
  color: var(--gray-9) !important;
  margin: 0 0 4px;
}
.terms-page .page-sub {
  font-size: clamp(13px, 1vw, 18px);
  color: var(--text-muted) !important;
  margin: 0;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}
.toolbar-l {
  display: flex;
  gap: 8px;
}

/* Buttons */
.btn {
  padding: 9px 18px;
  font-size: var(--font-size-base);
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid var(--gray-4);
  background: white;
  color: var(--text-secondary);
}
.btn:hover:not(:disabled) {
  border-color: var(--gray-5);
  color: var(--text-primary);
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}
.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  color: white;
}
.btn-primary.danger-primary {
  background: var(--red);
  border-color: var(--red);
}
.btn-primary.danger-primary:hover:not(:disabled) {
  background: var(--red-hover);
  border-color: var(--red-hover);
}
/* Link-style text button — color/weight give clear affordance even before hover. */
.btn-text {
  background: transparent;
  border: none;
  color: var(--primary);
  padding: 6px 8px;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.btn-text:hover:not(:disabled) {
  color: var(--primary-hover);
  background: var(--primary-bg);
}
.btn-text.danger {
  color: var(--red);
}
.btn-text.danger:hover:not(:disabled) {
  color: var(--red-hover);
  background: var(--red-bg);
}
.btn-text:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}
.btn-sm {
  padding: 5px 10px;
  font-size: var(--font-size-sm);
}
/* Spacing between sibling .btn-text in actions column for clearer separation. */
.terms-page .actions-col .btn-text + .btn-text {
  margin-left: 4px;
}
/* L3 — FAILED row uses '실패' tag (rollback unavailable) next to the [본문] button. */
.terms-page .actions-col .btn-text + .failed-tag {
  margin-left: 6px;
}

/* Status dot */
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  white-space: nowrap;
}
.status::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-5);
  display: inline-block;
}
.status.published::before {
  background: var(--green);
}
.status.never-published::before {
  background: var(--gray-5);
}
.status.publishing::before {
  background: var(--orange);
}
.status.failed::before {
  background: var(--red);
}
.status.failed {
  color: var(--red);
}
.muted {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* Version cell */
.v-cell {
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}
/* Version display — label first (1.7), 정수 v숫자 보조 (v8). */
.v-main {
  font-weight: 600;
  color: var(--text-primary);
}
.v-cell .v-main {
  font-size: var(--font-size-md);
}
.v-sub {
  margin-left: 6px;
  color: var(--text-muted);
  font-size: clamp(11px, 0.7vw, 13px);
}

/* Dialog inner helpers */
.dialog-paragraph {
  margin: 0 0 16px;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.dialog-hint {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 4px;
}
.dialog-progress {
  text-align: center;
  color: var(--text-muted);
  margin-top: 8px;
  font-size: var(--font-size-sm);
}
.version-body-frame {
  width: 100%;
  height: 60vh;
  border: 1px solid var(--gray-3);
  border-radius: 4px;
  background: #fff;
}
.mt-3 {
  margin-top: 16px;
}
</style>
<style>
/* Unscoped: el-table look */
.terms-page .versions-table.terms-table.el-table,
.terms-page .versions-table.terms-table.el-table--fit {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
}
.terms-page .versions-table.terms-table .el-table__header-wrapper th {
  background: var(--gray-1);
  color: var(--text-secondary);
  font-size: clamp(12px, 0.9vw, 16px) !important;
  font-weight: 500;
  border-bottom: 1px solid var(--gray-3);
  padding: 12px 0;
}
.terms-page .versions-table.terms-table td {
  padding: 14px 0;
  font-size: clamp(13px, 1vw, 18px) !important;
  line-height: var(--line-height-base);
  color: var(--text-primary);
}
.terms-page .versions-table.terms-table tr:hover > td {
  background: var(--gray-1) !important;
}
.terms-page .versions-table.terms-table .actions-col .cell {
  text-align: right;
}
/* Cell wrap policy
   - 기본은 normal (긴 메모 등 줄바꿈 허용)
   - 한 단위로 표시되어야 하는 cell만 nowrap (datetime / status / version / actions) */
.terms-page .versions-table.terms-table td .cell {
  white-space: normal;
  word-break: break-word;
}
.terms-page .versions-table.terms-table td.col-datetime .cell,
.terms-page .versions-table.terms-table td.col-status .cell,
.terms-page .versions-table.terms-table td.col-version .cell,
.terms-page .versions-table.terms-table td.col-actions .cell {
  white-space: nowrap;
}
.terms-page .versions-table.terms-table td.col-datetime .cell {
  font-variant-numeric: tabular-nums;
}
/* Reduce el-table empty-block min-height (default 60px). */
.terms-page .versions-table.terms-table .el-table__empty-block {
  min-height: 0;
}
.terms-page .versions-table.terms-table.el-table,
.terms-page .versions-table.terms-table.el-table--fit {
  flex: none !important;
  height: auto !important;
}
/* RETRY 19 — body-wrapper height reset + ::before pseudo-line hide.
   Mirrors Terms.vue empty-area override stack. */
.terms-page .versions-table.terms-table .el-table__body-wrapper {
  height: auto !important;
  min-height: 0 !important;
}
.terms-page .versions-table.terms-table.el-table::before {
  display: none;
}

/* Dialog (mockup look) — shared with Terms.vue */
.terms-dialog .el-dialog__header {
  padding: 18px 24px;
  border-bottom: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body {
  padding: 24px;
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.terms-dialog .el-dialog__body .el-input__inner {
  font-size: var(--font-size-base);
}
.terms-dialog .el-dialog__footer {
  padding: 14px 24px;
  border-top: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__footer .btn + .btn {
  margin-left: 8px;
}
</style>
